import type { PageHelp } from 'help/types';

export const importedInvoicesHelp: PageHelp = {
  title: 'Imported invoices',
  intro:
    'Import the invoices that QTG (the motor quoting tool of GrECo Latvia) issued to clients and see which installment of the payment plan each invoice entry belongs to. Matching by VIN, insurer, insurance line and premium happens on import and on re-match; this page is where you see which invoices are still unmatched and which were paid but could not be cleared yet. The page is shown only to users who work for a Latvian GrECo company.',
  sections: [
    'Getting around',
    'Finding an invoice',
    'Reading the invoice list',
    'Reading the entries',
    'Import the QTG export',
    'Re-match',
    'Delete an invoice',
  ],
  contexts: {
    'import-dialog': 'Click "Import QTG invoices" in the second toolbar row.',
    'pull-dialog': 'Click "Pull from QTG" in the second toolbar row.',
    'rematch-dialog': 'Click "Re-match all" in the second toolbar row, or the re-match icon in the last column of an invoice row (on a ticked row it acts on every ticked row).',
    'delete-dialog': 'Click the delete icon in the last column of an invoice row (on a ticked row it acts on every ticked row).',
  },
  tips: [
    'An invoice entry is one vehicle (member offer) of a QTG invoice: a line invoice has one entry, a group invoice one per vehicle. Each entry is matched to the installment of the same GrECo company whose VIN, insurer, insurance line and premium are the same; the client is not compared, because QTG and CRM may register the same person differently. Of several such installments the one whose booking date is closest to the invoice date wins. Matching is automatic only: an entry whose VIN, insurer, line or premium differs from GOS simply stays unmatched and the bank payment is cleared by hand on the Bank statements page.',
    'Match state: Unmatched (no entry has an installment), Partially matched, Matched (every entry has one, none paid), Pending (the invoice was paid by a bank statement entry that could not be cleared yet, usually because the policy is not in GOS yet) and Paid (every entry settled by a payment).',
    'Clearing a bank statement entry stays on the Bank statements page: when the payment reference contains a QTG invoice number, the clearance suggests the installments of that invoice first. Re-match never clears anything.',
    'Importing, re-matching and deleting need write access in PCT and access to the GrECo Latvia company; read-only users can browse the invoices, but the server refuses these changes.',
  ],
  steps: [
    // Getting around
    {
      selector: '[data-help="imported-invoices-page-switcher"]',
      title: 'Switch to another PCT page',
      body: 'The toolbar moves between the PCT pages: bank statements, insurer reports, imported invoices, client balance, payment plans, reminders and payments. Go to Bank statements to clear the payment of an invoice, and to Payment plans when an installment is missing or wrong.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Pereiti į kitą PCT puslapį',
          body: 'Įrankių juosta perjungia PCT puslapius: banko išrašus, draudiko ataskaitas, importuotas sąskaitas, kliento balansą, mokėjimo planus, priminimus ir mokėjimus. Eikite į banko išrašus, kad užskaitytumėte sąskaitos mokėjimą, ir į mokėjimo planus, kai įmokos trūksta arba ji neteisinga.',
        },
        lv: {
          title: 'Pāriet uz citu PCT lapu',
          body: 'Rīkjosla pārslēdz PCT lapas: bankas izrakstus, apdrošinātāja atskaites, importētos rēķinus, klienta bilanci, maksājumu plānus, atgādinājumus un maksājumus. Dodieties uz bankas izrakstiem, lai nokārtotu rēķina maksājumu, un uz maksājumu plāniem, ja iemaksas trūkst vai tā ir nepareiza.',
        },
      },
    },
    {
      selector: '[data-help="app-user-manual"]',
      title: 'Download the user manual',
      body: 'Downloads the complete PCT user manual as a PDF, with screenshots of every page. You get it in the language selected in Settings: Lithuanian or Latvian when the app is in that language, English otherwise.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Atsisiųsti naudotojo vadovą',
          body: 'Atsisiunčia visą PCT naudotojo vadovą PDF formatu su visų puslapių ekrano kopijomis. Gaunate jį kalba, pasirinkta „Nustatymuose“: lietuvių arba latvių, kai programa veikia ta kalba, kitu atveju – anglų.',
        },
        lv: {
          title: 'Lejupielādēt lietotāja rokasgrāmatu',
          body: 'Lejupielādē pilnu PCT lietotāja rokasgrāmatu PDF formātā ar katras lapas ekrānuzņēmumiem. Jūs to saņemat Iestatījumos izvēlētajā valodā: lietuviešu vai latviešu, ja lietotne darbojas tajā valodā, citādi angļu.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-refresh"]',
      title: 'Reload the invoice list',
      body: 'Runs the query again with the current issue dates and match state. Use it after a colleague imported a file or after the GOS synchronisation brought new policies.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Iš naujo įkelti sąskaitų sąrašą',
          body: 'Dar kartą vykdo užklausą su dabartinėmis išrašymo datomis ir sąsajos būsena. Naudokite, kai kolega importavo failą arba GOS sinchronizacija atnešė naujus polisus.',
        },
        lv: {
          title: 'Pārlādēt rēķinu sarakstu',
          body: 'Vēlreiz izpilda vaicājumu ar pašreizējiem izrakstīšanas datumiem un sasaistes stāvokli. Izmantojiet to, kad kolēģis ir importējis failu vai GOS sinhronizācija ir atnesusi jaunas polises.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-import-button"]',
      title: 'Import a QTG export',
      body: 'Opens the import dialog for the Excel file that QTG produces with "Export invoices" on its offers page. The same period can be imported again at any time: known invoices are refreshed, not duplicated.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Importuoti QTG eksportą',
          body: 'Atidaro importo langą Excel failui, kurį QTG sukuria pasiūlymų puslapyje paspaudus „Eksportuoti sąskaitas“. Tą patį laikotarpį galima importuoti dar kartą: žinomos sąskaitos atnaujinamos, o ne dubliuojamos.',
        },
        lv: {
          title: 'Importēt QTG eksportu',
          body: 'Atver importa dialogu Excel failam, ko QTG izveido piedāvājumu lapā ar “Eksportēt rēķinus”. To pašu periodu var importēt atkārtoti jebkurā laikā: zināmie rēķini tiek atjaunināti, nevis dublēti.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-pull-button"]',
      title: 'Pull the invoices from QTG',
      body: 'Opens the pull dialog: PCT fetches the invoices QTG issued in a period you choose directly from QTG, without an Excel file. The same rules apply as for an uploaded file. Every night PCT also pulls the invoices issued since the day before, so the button is for a period of your own or for not waiting until then.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Gauti sąskaitas iš QTG',
          body: 'Atidaro gavimo langą: PCT gauna jūsų pasirinkto laikotarpio QTG išrašytas sąskaitas tiesiai iš QTG, be Excel failo. Galioja tos pačios taisyklės kaip įkeltam failui. Kas naktį PCT taip pat gauna nuo vakar dienos išrašytas sąskaitas, todėl mygtukas skirtas savam laikotarpiui arba tam, kad nereikėtų laukti.',
        },
        lv: {
          title: 'Ielādēt rēķinus no QTG',
          body: 'Atver ielādes dialogu: PCT saņem jūsu izvēlētā perioda QTG izrakstītos rēķinus tieši no QTG, bez Excel faila. Spēkā tie paši noteikumi kā augšupielādētam failam. Katru nakti PCT ielādē arī kopš iepriekšējās dienas izrakstītos rēķinus, tāpēc poga ir savam periodam vai tam, lai nebūtu jāgaida.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-rematch-all-button"]',
      title: 'Re-match every open invoice',
      body: 'Matches the entries of every invoice that is not fully matched again against the installments in PCT. Use it after the GOS synchronisation, when policies that were missing at import time have arrived.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Iš naujo susieti visas atviras sąskaitas',
          body: 'Iš naujo susieja kiekvienos ne iki galo susietos sąskaitos įrašus su PCT įmokomis. Naudokite po GOS sinchronizacijos, kai atkeliavo polisai, kurių importo metu trūko.',
        },
        lv: {
          title: 'Atkārtoti sasaistīt visus atvērtos rēķinus',
          body: 'Atkārtoti sasaista katra ne pilnībā sasaistītā rēķina ierakstus ar PCT iemaksām. Izmantojiet pēc GOS sinhronizācijas, kad ir pienākušas polises, kuru importa brīdī trūka.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-export-excel"]',
      title: 'Export to Excel',
      body: 'Saves the invoices currently shown in the list as an Excel file, one row per entry, with the invoice columns followed by the entry columns and the matched installment. Red cells mark premium differences.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Eksportuoti į Excel',
          body: 'Išsaugo sąraše rodomas sąskaitas kaip Excel failą, po vieną eilutę kiekvienam įrašui: sąskaitos stulpeliai, įrašo stulpeliai ir susieta įmoka. Raudoni langeliai žymi premijos skirtumus.',
        },
        lv: {
          title: 'Eksportēt uz Excel',
          body: 'Saglabā sarakstā redzamos rēķinus kā Excel failu, pa vienai rindai katram ierakstam: rēķina kolonnas, ieraksta kolonnas un sasaistītā iemaksa. Sarkanās šūnas atzīmē prēmijas atšķirības.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-results-count"]',
      title: 'Number of invoices shown',
      body: 'Counts the invoices visible in the list after the search box filtered them. It changes as you type in the search box.',
      section: 'Getting around',
      i18n: {
        lt: {
          title: 'Rodomų sąskaitų skaičius',
          body: 'Skaičiuoja sąraše matomas sąskaitas po paieškos lauko filtravimo. Keičiasi rašant paieškos lauke.',
        },
        lv: {
          title: 'Rādīto rēķinu skaits',
          body: 'Skaita sarakstā redzamos rēķinus pēc meklēšanas lauka filtrēšanas. Mainās, rakstot meklēšanas laukā.',
        },
      },
    },
    // Finding an invoice
    {
      selector: '[data-help="imported-invoices-search"]',
      title: 'Search the loaded invoices',
      body: 'Filters the loaded invoices on the screen by invoice number, QTG id, payer, match state and by the VIN, client, insurer or policy number of their entries. Paste the VIN or the invoice number from a bank statement entry to find the invoice it paid.',
      section: 'Finding an invoice',
      i18n: {
        lt: {
          title: 'Ieškoti įkeltose sąskaitose',
          body: 'Filtruoja ekrane įkeltas sąskaitas pagal sąskaitos numerį, QTG id, mokėtoją, sąsajos būseną ir pagal įrašų VIN, klientą, draudiką ar poliso numerį. Įklijuokite VIN arba sąskaitos numerį iš banko išrašo įrašo, kad rastumėte apmokėtą sąskaitą.',
        },
        lv: {
          title: 'Meklēt ielādētajos rēķinos',
          body: 'Filtrē ekrānā ielādētos rēķinus pēc rēķina numura, QTG id, maksātāja, sasaistes stāvokļa un pēc ierakstu VIN, klienta, apdrošinātāja vai polises numura. Ielīmējiet VIN vai rēķina numuru no bankas izraksta ieraksta, lai atrastu apmaksāto rēķinu.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-from-date"]',
      title: 'Issued from',
      body: 'First issue date of the invoices to load. The list is reloaded from the server when the date changes; the default is three months back.',
      section: 'Finding an invoice',
      i18n: {
        lt: {
          title: 'Išrašytos nuo',
          body: 'Pirmoji įkeliamų sąskaitų išrašymo data. Pakeitus datą sąrašas iš naujo įkeliamas iš serverio; numatytoji reikšmė – prieš tris mėnesius.',
        },
        lv: {
          title: 'Izrakstīti no',
          body: 'Pirmais ielādējamo rēķinu izrakstīšanas datums. Mainot datumu, saraksts tiek pārlādēts no servera; noklusējums ir trīs mēneši atpakaļ.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-to-date"]',
      title: 'Issued to',
      body: 'Last issue date of the invoices to load. Together with "Issued from" it limits the query to a period; the default is today.',
      section: 'Finding an invoice',
      i18n: {
        lt: {
          title: 'Išrašytos iki',
          body: 'Paskutinė įkeliamų sąskaitų išrašymo data. Kartu su „Išrašytos nuo“ apriboja užklausą laikotarpiu; numatytoji reikšmė – šiandien.',
        },
        lv: {
          title: 'Izrakstīti līdz',
          body: 'Pēdējais ielādējamo rēķinu izrakstīšanas datums. Kopā ar “Izrakstīti no” tas ierobežo vaicājumu līdz periodam; noklusējums ir šodiena.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-match-state"]',
      title: 'Filter by match state',
      body: 'Loads only the invoices in one match state. "Unmatched" and "Partially matched" are the ones that need your attention; "Pending" lists invoices paid by a bank statement entry that is still open.',
      section: 'Finding an invoice',
      i18n: {
        lt: {
          title: 'Filtruoti pagal sąsajos būseną',
          body: 'Įkelia tik vienos sąsajos būsenos sąskaitas. „Nesusieta“ ir „Iš dalies susieta“ reikalauja jūsų dėmesio; „Laukiama“ rodo sąskaitas, apmokėtas dar atviru banko išrašo įrašu.',
        },
        lv: {
          title: 'Filtrēt pēc sasaistes stāvokļa',
          body: 'Ielādē tikai viena sasaistes stāvokļa rēķinus. “Nesasaistīts” un “Daļēji sasaistīts” prasa jūsu uzmanību; “Gaida” rāda rēķinus, kas apmaksāti ar vēl atvērtu bankas izraksta ierakstu.',
        },
      },
    },
    // Reading the invoice list
    {
      selector: '[data-help="imported-invoices-grid"]',
      title: 'The invoice list',
      body: 'One row per QTG invoice: number, match state (red while it needs attention), issue and due dates, payer, premium, whether it is a group invoice, the entry counts, the date it was marked paid in QTG, the open bank statement entry that paid it (booking date, amount and payer), and when and from which file it was imported. Tick the checkbox of a row to see its entries below; tick several rows, or the checkbox in the header for every row in the list, to re-match or delete them at once.',
      section: 'Reading the invoice list',
      i18n: {
        lt: {
          title: 'Sąskaitų sąrašas',
          body: 'Po vieną eilutę kiekvienai QTG sąskaitai: numeris, sąsajos būsena (raudona, kol reikia dėmesio), išrašymo ir apmokėjimo terminai, mokėtojas, premija, ar tai grupinė sąskaita, įrašų skaičiai, data, kada QTG pažymėta kaip apmokėta, atviras banko išrašo įrašas, kuriuo ji apmokėta (data, suma ir mokėtojas), ir kada bei iš kokio failo importuota. Pažymėkite eilutės langelį, kad apačioje matytumėte jos įrašus; pažymėkite kelias eilutes arba antraštės langelį (visos sąrašo eilutės), kad jas iš karto susietumėte iš naujo arba ištrintumėte.',
        },
        lv: {
          title: 'Rēķinu saraksts',
          body: 'Pa vienai rindai katram QTG rēķinam: numurs, sasaistes stāvoklis (sarkans, kamēr nepieciešama uzmanība), izrakstīšanas un apmaksas termiņi, maksātājs, prēmija, vai tas ir grupas rēķins, ierakstu skaits, datums, kad QTG atzīmēts kā apmaksāts, atvērtais bankas izraksta ieraksts, ar kuru tas apmaksāts (datums, summa un maksātājs), un kad un no kura faila importēts. Atzīmējiet rindas izvēles rūtiņu, lai zemāk redzētu tās ierakstus; atzīmējiet vairākas rindas vai galvenes rūtiņu (visas saraksta rindas), lai tās uzreiz sasaistītu atkārtoti vai dzēstu.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-row-rematch"]',
      title: 'Re-match this invoice, or the ticked invoices',
      body: 'Matches the entries of this invoice again against the installments in PCT and retries the CRM lookup of its insurer, client and payer codes. On a ticked row the icon acts on every ticked row at once (the tooltip shows the count). No bank statement entry is cleared.',
      section: 'Reading the invoice list',
      i18n: {
        lt: {
          title: 'Iš naujo susieti šią arba pažymėtas sąskaitas',
          body: 'Iš naujo susieja šios sąskaitos įrašus su PCT įmokomis ir dar kartą ieško jos draudiko, kliento ir mokėtojo kodų CRM. Pažymėtoje eilutėje piktograma veikia visas pažymėtas eilutes iš karto (patarime rodomas skaičius). Joks banko išrašo įrašas neužskaitomas.',
        },
        lv: {
          title: 'Atkārtoti sasaistīt šo vai atzīmētos rēķinus',
          body: 'Atkārtoti sasaista šī rēķina ierakstus ar PCT iemaksām un vēlreiz meklē tā apdrošinātāja, klienta un maksātāja kodus CRM. Atzīmētā rindā ikona darbojas uz visām atzīmētajām rindām uzreiz (rīka padomā redzams skaits). Neviens bankas izraksta ieraksts netiek nokārtots.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-row-delete"]',
      title: 'Delete this invoice, or the ticked invoices',
      body: 'Removes the invoice and its entries from PCT. On a ticked row the icon acts on every ticked row at once (the tooltip shows the count). The invoice in QTG is not affected, and the next import of a file that contains it brings it back.',
      section: 'Reading the invoice list',
      i18n: {
        lt: {
          title: 'Ištrinti šią arba pažymėtas sąskaitas',
          body: 'Pašalina sąskaitą ir jos įrašus iš PCT. Pažymėtoje eilutėje piktograma veikia visas pažymėtas eilutes iš karto (patarime rodomas skaičius). Sąskaita QTG nepaveikiama, o kitas failo, kuriame ji yra, importas ją sugrąžina.',
        },
        lv: {
          title: 'Dzēst šo vai atzīmētos rēķinus',
          body: 'Noņem rēķinu un tā ierakstus no PCT. Atzīmētā rindā ikona darbojas uz visām atzīmētajām rindām uzreiz (rīka padomā redzams skaits). Rēķins QTG netiek ietekmēts, un nākamais imports ar failu, kurā tas ir, to atgriež.',
        },
      },
    },
    // Reading the entries
    {
      selector: '[data-help="imported-invoices-entries-search"]',
      title: 'Search the entries of the selected invoice',
      body: 'Filters the entries shown below by VIN, insurer, client, client code, offer id or the policy number of the matched installment. Disabled until an invoice is selected in the list above.',
      section: 'Reading the entries',
      i18n: {
        lt: {
          title: 'Ieškoti pasirinktos sąskaitos įrašuose',
          body: 'Filtruoja žemiau rodomus įrašus pagal VIN, draudiką, klientą, kliento kodą, pasiūlymo id arba susietos įmokos poliso numerį. Neaktyvu, kol viršuje nepasirinkta sąskaita.',
        },
        lv: {
          title: 'Meklēt izvēlētā rēķina ierakstos',
          body: 'Filtrē zemāk redzamos ierakstus pēc VIN, apdrošinātāja, klienta, klienta koda, piedāvājuma id vai sasaistītās iemaksas polises numura. Neaktīvs, kamēr sarakstā augšā nav izvēlēts rēķins.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-entries-count"]',
      title: 'Number of entries shown',
      body: 'Counts the entries of the selected invoice that are visible after the entry search box filtered them.',
      section: 'Reading the entries',
      i18n: {
        lt: {
          title: 'Rodomų įrašų skaičius',
          body: 'Skaičiuoja pasirinktos sąskaitos įrašus, matomus po įrašų paieškos lauko filtravimo.',
        },
        lv: {
          title: 'Rādīto ierakstu skaits',
          body: 'Skaita izvēlētā rēķina ierakstus, kas redzami pēc ierakstu meklēšanas lauka filtrēšanas.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-entries-grid"]',
      title: 'The entries of the selected invoice',
      body: 'One row per vehicle of the invoice: VIN, insurance line, insurer and client as exported by QTG (with the CRM partner when the code was found), the premium, and the matched installment with its policy number, premium, client, start date and paid flag. Orange rows are matched; "VIN hits" in red means installments with that VIN exist in GOS but none agrees on insurer, line and premium, so this entry will not match automatically and its payment is cleared by hand.',
      section: 'Reading the entries',
      i18n: {
        lt: {
          title: 'Pasirinktos sąskaitos įrašai',
          body: 'Po vieną eilutę kiekvienai sąskaitos transporto priemonei: VIN, draudimo rūšis, draudikas ir klientas, kaip eksportavo QTG (su CRM partneriu, kai kodas rastas), premija ir susieta įmoka su poliso numeriu, premija, klientu, pradžios data ir apmokėjimo požymiu. Oranžinės eilutės susietos; raudonas „VIN atitikmenys“ reiškia, kad įmokų su tuo VIN GOS yra, bet nė viena nesutampa pagal draudiką, rūšį ir premiją, todėl šis įrašas automatiškai nesusies ir jo mokėjimas užskaitomas ranka.',
        },
        lv: {
          title: 'Izvēlētā rēķina ieraksti',
          body: 'Pa vienai rindai katram rēķina transportlīdzeklim: VIN, apdrošināšanas veids, apdrošinātājs un klients, kā eksportējis QTG (ar CRM partneri, ja kods atrasts), prēmija un sasaistītā iemaksa ar polises numuru, prēmiju, klientu, sākuma datumu un apmaksas pazīmi. Oranžās rindas ir sasaistītas; sarkans “VIN atbilstības” nozīmē, ka iemaksas ar šo VIN GOS pastāv, bet neviena nesakrīt pēc apdrošinātāja, veida un prēmijas, tāpēc šis ieraksts automātiski nesasaistīsies un tā maksājums tiek nokārtots manuāli.',
        },
      },
    },
    // Import the QTG export
    {
      selector: '[data-help="imported-invoices-import-info"]',
      title: 'What the import does',
      body: 'Reminds you where the file comes from and what happens to it: known invoices (same QTG id) are refreshed, new ones are added, and every entry is matched to the installment with the same VIN, insurer, insurance line and premium.',
      section: 'Import the QTG export',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Ką daro importas',
          body: 'Primena, iš kur failas ir kas su juo vyksta: žinomos sąskaitos (tas pats QTG id) atnaujinamos, naujos pridedamos, o kiekvienas įrašas susiejamas su įmoka, turinčia tą patį VIN, draudiką, draudimo rūšį ir premiją.',
        },
        lv: {
          title: 'Ko dara imports',
          body: 'Atgādina, no kurienes fails nāk un kas ar to notiek: zināmie rēķini (tas pats QTG id) tiek atjaunināti, jaunie pievienoti, un katrs ieraksts tiek sasaistīts ar iemaksu, kurai ir tas pats VIN, apdrošinātājs, apdrošināšanas veids un prēmija.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-import-file"]',
      title: 'Choose the export file',
      body: 'Drag the xlsx file that QTG exported into the box or click "Browse Files". Invoices already in PCT are replaced by the data in the file; invoices missing from the file are left as they are. The first sheet needs the columns Id, Number, Issued, DueDate, Premium, Vins, Premiums, InsuranceLines, InsurerCodes and ClientCodes; the list columns are separated by "|".',
      section: 'Import the QTG export',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Pasirinkti eksporto failą',
          body: 'Nutempkite QTG eksportuotą xlsx failą į langą arba spauskite „Ieškoti“. PCT jau esančios sąskaitos pakeičiamos failo duomenimis; sąskaitos, kurių faile nėra, lieka nepakitusios. Pirmame lape turi būti stulpeliai Id, Number, Issued, DueDate, Premium, Vins, Premiums, InsuranceLines, InsurerCodes ir ClientCodes; sąrašų stulpeliai atskirti „|“.',
        },
        lv: {
          title: 'Izvēlēties eksporta failu',
          body: 'Ievelciet QTG eksportēto xlsx failu logā vai noklikšķiniet uz “Browse Files”. PCT jau esošie rēķini tiek aizstāti ar faila datiem; rēķini, kuru failā nav, paliek neskarti. Pirmajā lapā jābūt kolonnām Id, Number, Issued, DueDate, Premium, Vins, Premiums, InsuranceLines, InsurerCodes un ClientCodes; saraksta kolonnas atdalītas ar “|”.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-import-submit"]',
      title: 'Run the import',
      body: 'Uploads the file. The whole file is imported or nothing is: a row whose list columns are not aligned or whose premium does not add up stops the import with the row number in the message.',
      section: 'Import the QTG export',
      context: 'import-dialog',
      i18n: {
        lt: {
          title: 'Vykdyti importą',
          body: 'Įkelia failą. Importuojamas visas failas arba nieko: eilutė, kurios sąrašų stulpeliai nesutampa arba premija nesusideda, sustabdo importą ir pranešime nurodomas eilutės numeris.',
        },
        lv: {
          title: 'Veikt importu',
          body: 'Augšupielādē failu. Tiek importēts viss fails vai nekas: rinda, kuras saraksta kolonnas nesakrīt vai kuras prēmija nesummējas, aptur importu, un ziņojumā ir rindas numurs.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-import-result"]',
      title: 'Read the import report',
      body: 'After the import the dialog shows how many invoices were new and how many replaced, how many entries were matched and how many are still open, and the insurer, client and payer codes that were not found in CRM. Unresolved codes do not block the import: re-match retries them once the partner exists.',
      section: 'Import the QTG export',
      context: 'import-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Perskaityti importo ataskaitą',
          body: 'Po importo langas rodo, kiek sąskaitų naujų ir kiek pakeistų, kiek įrašų susieta ir kiek dar atvira, bei draudikų, klientų ir mokėtojų kodus, nerastus CRM. Nerasti kodai importo nestabdo: pakartotinis susiejimas bando iš naujo, kai partneris atsiranda.',
        },
        lv: {
          title: 'Izlasīt importa atskaiti',
          body: 'Pēc importa dialogs rāda, cik rēķinu ir jauni un cik aizstāti, cik ierakstu sasaistīti un cik vēl atvērti, kā arī apdrošinātāju, klientu un maksātāju kodus, kas CRM nav atrasti. Neatrastie kodi importu nebloķē: atkārtota sasaistīšana tos mēģina vēlreiz, kad partneris pastāv.',
        },
      },
    },
    // Pull from QTG
    {
      selector: '[data-help="imported-invoices-pull-info"]',
      title: 'What the pull does',
      body: 'Reminds you what is fetched: the invoices QTG issued in the chosen period, straight from QTG. Known invoices (same QTG id) are replaced, new ones are added, and every entry is matched to the installment with the same VIN, insurer, insurance line and premium. Nothing is deleted when an invoice is not in the answer.',
      section: 'Import the QTG export',
      context: 'pull-dialog',
      i18n: {
        lt: {
          title: 'Ką daro gavimas',
          body: 'Primena, kas gaunama: pasirinkto laikotarpio QTG išrašytos sąskaitos, tiesiai iš QTG. Žinomos sąskaitos (tas pats QTG id) pakeičiamos, naujos pridedamos, o kiekvienas įrašas susiejamas su įmoka, turinčia tą patį VIN, draudiką, draudimo rūšį ir premiją. Niekas netrinama, kai sąskaitos atsakyme nėra.',
        },
        lv: {
          title: 'Ko dara ielāde',
          body: 'Atgādina, kas tiek saņemts: izvēlētajā periodā QTG izrakstītie rēķini, tieši no QTG. Zināmie rēķini (tas pats QTG id) tiek aizstāti, jaunie pievienoti, un katrs ieraksts tiek sasaistīts ar iemaksu, kurai ir tas pats VIN, apdrošinātājs, apdrošināšanas veids un prēmija. Nekas netiek dzēsts, ja rēķina atbildē nav.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-pull-from-date"]',
      title: 'Issued from',
      body: 'First issue date of the invoices to fetch. Mandatory; proposed as seven days ago. A period can be pulled again at any time: known invoices are replaced, not duplicated.',
      section: 'Import the QTG export',
      context: 'pull-dialog',
      i18n: {
        lt: {
          title: 'Išrašytos nuo',
          body: 'Pirmoji gaunamų sąskaitų išrašymo data. Privaloma; siūloma prieš septynias dienas. Laikotarpį galima gauti dar kartą bet kada: žinomos sąskaitos pakeičiamos, o ne dubliuojamos.',
        },
        lv: {
          title: 'Izrakstīti no',
          body: 'Pirmais saņemamo rēķinu izrakstīšanas datums. Obligāts; piedāvāts pirms septiņām dienām. Periodu var ielādēt atkārtoti jebkurā laikā: zināmie rēķini tiek aizstāti, nevis dublēti.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-pull-to-date"]',
      title: 'Issued to',
      body: 'Last issue date of the invoices to fetch. Mandatory; proposed as today, not before "Issued from", and at most one month after it: a longer period is refused, pull it in parts.',
      section: 'Import the QTG export',
      context: 'pull-dialog',
      i18n: {
        lt: {
          title: 'Išrašytos iki',
          body: 'Paskutinė gaunamų sąskaitų išrašymo data. Privaloma; siūloma šiandien, ne anksčiau nei „Išrašytos nuo“ ir ne vėliau nei mėnuo po jos: ilgesnis laikotarpis atmetamas, gaukite jį dalimis.',
        },
        lv: {
          title: 'Izrakstīti līdz',
          body: 'Pēdējais saņemamo rēķinu izrakstīšanas datums. Obligāts; piedāvāts šodien, ne agrāk par “Izrakstīti no” un ne vēlāk par mēnesi pēc tā: garāks periods tiek noraidīts, ielādējiet to pa daļām.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-pull-submit"]',
      title: 'Run the pull',
      body: 'Asks QTG for the invoices of the period and imports them in one go. A period with no invoices is not an error: the report then shows zero rows. A connection or configuration problem is shown as an error and nothing is changed.',
      section: 'Import the QTG export',
      context: 'pull-dialog',
      i18n: {
        lt: {
          title: 'Vykdyti gavimą',
          body: 'Paprašo QTG laikotarpio sąskaitų ir jas iš karto importuoja. Laikotarpis be sąskaitų nėra klaida: ataskaita tada rodo nulį eilučių. Ryšio ar konfigūracijos problema rodoma kaip klaida ir niekas nekeičiama.',
        },
        lv: {
          title: 'Veikt ielādi',
          body: 'Pieprasa QTG perioda rēķinus un tos uzreiz importē. Periods bez rēķiniem nav kļūda: atskaite tad rāda nulli rindu. Savienojuma vai konfigurācijas problēma tiek rādīta kā kļūda, un nekas netiek mainīts.',
        },
      },
    },
    {
      selector: '[data-help="imported-invoices-pull-result"]',
      title: 'Read the pull report',
      body: 'The first line tells how many invoices QTG answered with and for which issue dates; the rest is the same report as after an upload: new and replaced invoices, matched and open entries, and the codes not found in CRM.',
      section: 'Import the QTG export',
      context: 'pull-dialog',
      optional: true,
      i18n: {
        lt: {
          title: 'Perskaityti gavimo ataskaitą',
          body: 'Pirmoje eilutėje nurodoma, kiek sąskaitų atsiuntė QTG ir už kokias išrašymo datas; toliau ta pati ataskaita kaip po įkėlimo: naujos ir pakeistos sąskaitos, susieti ir atviri įrašai bei CRM nerasti kodai.',
        },
        lv: {
          title: 'Izlasīt ielādes atskaiti',
          body: 'Pirmajā rindā norādīts, cik rēķinu QTG atbildēja un par kuriem izrakstīšanas datumiem; pārējais ir tā pati atskaite kā pēc augšupielādes: jaunie un aizstātie rēķini, sasaistītie un atvērtie ieraksti un CRM neatrastie kodi.',
        },
      },
    },
    // Re-match
    {
      selector: '[data-help="imported-invoices-rematch-confirm"]',
      title: 'Confirm the re-match',
      body: 'Runs the automatic matching again for one invoice, for the ticked invoices or for every invoice that is not fully matched, and retries the CRM lookup of unresolved codes. Nothing is cleared: a pending invoice still needs its bank statement entry cleared on the Bank statements page.',
      section: 'Re-match',
      context: 'rematch-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti pakartotinį susiejimą',
          body: 'Dar kartą vykdo automatinį susiejimą vienai sąskaitai, pažymėtoms sąskaitoms arba visoms ne iki galo susietoms sąskaitoms ir iš naujo ieško nerastų kodų CRM. Niekas neužskaitoma: laukiančios sąskaitos banko išrašo įrašą vis tiek reikia užskaityti banko išrašų puslapyje.',
        },
        lv: {
          title: 'Apstiprināt atkārtotu sasaistīšanu',
          body: 'Vēlreiz izpilda automātisko sasaistīšanu vienam rēķinam, atzīmētajiem rēķiniem vai visiem ne pilnībā sasaistītajiem rēķiniem un vēlreiz meklē neatrastos kodus CRM. Nekas netiek nokārtots: gaidoša rēķina bankas izraksta ieraksts joprojām jānokārto bankas izrakstu lapā.',
        },
      },
    },
    // Delete an invoice
    {
      selector: '[data-help="imported-invoices-delete-confirm"]',
      title: 'Confirm the deletion',
      body: 'Removes the invoice, or every ticked invoice, together with its entries from PCT in one go. Links to installments are dropped; payments are not touched. The invoices in QTG are not affected.',
      section: 'Delete an invoice',
      context: 'delete-dialog',
      i18n: {
        lt: {
          title: 'Patvirtinti ištrynimą',
          body: 'Iš karto pašalina sąskaitą arba visas pažymėtas sąskaitas su jų įrašais iš PCT. Ryšiai su įmokomis panaikinami; mokėjimai neliečiami. Sąskaitos QTG nepaveikiamos.',
        },
        lv: {
          title: 'Apstiprināt dzēšanu',
          body: 'Vienā reizē noņem rēķinu vai visus atzīmētos rēķinus ar to ierakstiem no PCT. Saites ar iemaksām tiek dzēstas; maksājumi netiek aiztikti. Rēķini QTG netiek ietekmēti.',
        },
      },
    },
  ],
  i18n: {
    lt: {
      title: 'Importuotos sąskaitos',
      intro:
        'Importuokite sąskaitas, kurias QTG (GrECo Latvia motorinių transporto priemonių draudimo pasiūlymų įrankis) išrašė klientams, ir matykite, kuriai mokėjimo plano įmokai priklauso kiekvienas sąskaitos įrašas. Susiejimas pagal VIN, draudiką, draudimo rūšį ir premiją vyksta importuojant ir pakartotinai susiejant; šiame puslapyje matoma, kurios sąskaitos dar nesusietos ir kurios apmokėtos, bet dar negalėjo būti užskaitytos. Puslapis rodomas tik naudotojams, dirbantiems su Latvijos GrECo įmone.',
      sections: {
        'Getting around': 'Navigacija',
        'Finding an invoice': 'Sąskaitos paieška',
        'Reading the invoice list': 'Sąskaitų sąrašo skaitymas',
        'Reading the entries': 'Įrašų skaitymas',
        'Import the QTG export': 'QTG eksporto importas',
        'Re-match': 'Pakartotinis susiejimas',
        'Delete an invoice': 'Sąskaitos ištrynimas',
      },
      contexts: {
        'import-dialog': 'Antroje įrankių juostos eilutėje spustelėkite „Importuoti QTG sąskaitas“.',
        'pull-dialog': 'Antroje įrankių juostos eilutėje spustelėkite „Gauti iš QTG“.',
        'rematch-dialog': 'Antroje įrankių juostos eilutėje spustelėkite „Susieti visas iš naujo“ arba paskutiniame sąskaitos eilutės stulpelyje – pakartotinio susiejimo piktogramą.',
        'delete-dialog': 'Paskutiniame sąskaitos eilutės stulpelyje spustelėkite ištrynimo piktogramą.',
      },
      tips: [
        'Sąskaitos įrašas yra viena QTG sąskaitos transporto priemonė (pasiūlymas): paprasta sąskaita turi vieną įrašą, grupinė – po vieną kiekvienai transporto priemonei. Kiekvienas įrašas susiejamas su tos pačios GrECo įmonės įmoka, kurios VIN, draudikas, draudimo rūšis ir premija sutampa; klientas nelyginamas, nes QTG ir CRM tą patį asmenį gali registruoti skirtingai. Iš kelių tokių įmokų laimi ta, kurios įtraukimo data arčiausia sąskaitos datos. Susiejimas tik automatinis: įrašas, kurio VIN, draudikas, rūšis ar premija skiriasi nuo GOS, tiesiog lieka nesusietas, o banko mokėjimas užskaitomas ranka banko išrašų puslapyje.',
        'Sąsajos būsena: Nesusieta (nė vienas įrašas neturi įmokos), Iš dalies susieta, Susieta (kiekvienas įrašas turi įmoką, nė vienas neapmokėtas), Laukiama (sąskaita apmokėta banko išrašo įrašu, kurio dar nepavyko užskaityti, dažniausiai nes poliso dar nėra GOS) ir Apmokėta (kiekvienas įrašas apmokėtas mokėjimu).',
        'Banko išrašo įrašo užskaitymas lieka banko išrašų puslapyje: kai mokėjimo paskirtyje yra QTG sąskaitos numeris, užskaitymas pirmiausia siūlo tos sąskaitos įmokas. Pakartotinis susiejimas niekada nieko neužskaito.',
        'Importui, pakartotiniam susiejimui ir ištrynimui reikia rašymo teisių PCT ir prieigos prie GrECo Latvia įmonės; tik skaityti galintys naudotojai gali peržiūrėti sąskaitas, bet serveris šiuos pakeitimus atmeta.',
      ],
    },
    lv: {
      title: 'Importētie rēķini',
      intro:
        'Importējiet rēķinus, ko QTG (GrECo Latvia auto apdrošināšanas piedāvājumu rīks) izrakstīja klientiem, un redziet, kurai maksājumu plāna iemaksai pieder katrs rēķina ieraksts. Sasaistīšana pēc VIN, apdrošinātāja, apdrošināšanas veida un prēmijas notiek importējot un atkārtoti sasaistot; šajā lapā redzams, kuri rēķini vēl nav sasaistīti un kuri ir apmaksāti, bet vēl nevarēja tikt nokārtoti. Lapa tiek rādīta tikai lietotājiem, kas strādā ar Latvijas GrECo uzņēmumu.',
      sections: {
        'Getting around': 'Navigācija',
        'Finding an invoice': 'Rēķina meklēšana',
        'Reading the invoice list': 'Rēķinu saraksta lasīšana',
        'Reading the entries': 'Ierakstu lasīšana',
        'Import the QTG export': 'QTG eksporta imports',
        'Re-match': 'Atkārtota sasaistīšana',
        'Delete an invoice': 'Rēķina dzēšana',
      },
      contexts: {
        'import-dialog': 'Otrajā rīkjoslas rindā noklikšķiniet uz “Importēt QTG rēķinus”.',
        'pull-dialog': 'Otrajā rīkjoslas rindā noklikšķiniet uz “Ielādēt no QTG”.',
        'rematch-dialog': 'Otrajā rīkjoslas rindā noklikšķiniet uz “Sasaistīt visus atkārtoti” vai rēķina rindas pēdējā kolonnā – uz atkārtotas sasaistīšanas ikonas.',
        'delete-dialog': 'Rēķina rindas pēdējā kolonnā noklikšķiniet uz dzēšanas ikonas.',
      },
      tips: [
        'Rēķina ieraksts ir viens QTG rēķina transportlīdzeklis (piedāvājums): parastam rēķinam ir viens ieraksts, grupas rēķinam – pa vienam katram transportlīdzeklim. Katrs ieraksts tiek sasaistīts ar tā paša GrECo uzņēmuma iemaksu, kurai sakrīt VIN, apdrošinātājs, apdrošināšanas veids un prēmija; klients netiek salīdzināts, jo QTG un CRM vienu un to pašu personu var reģistrēt atšķirīgi. No vairākām šādām iemaksām uzvar tā, kuras grāmatošanas datums ir tuvāk rēķina datumam. Sasaistīšana ir tikai automātiska: ieraksts, kura VIN, apdrošinātājs, veids vai prēmija atšķiras no GOS, vienkārši paliek nesasaistīts, un bankas maksājums tiek nokārtots manuāli bankas izrakstu lapā.',
        'Sasaistes stāvoklis: Nesasaistīts (nevienam ierakstam nav iemaksas), Daļēji sasaistīts, Sasaistīts (katram ierakstam ir iemaksa, neviens nav apmaksāts), Gaida (rēķins apmaksāts ar bankas izraksta ierakstu, ko vēl nevarēja nokārtot, parasti tāpēc, ka polises GOS vēl nav) un Apmaksāts (katrs ieraksts nokārtots ar maksājumu).',
        'Bankas izraksta ieraksta nokārtošana paliek bankas izrakstu lapā: ja maksājuma mērķī ir QTG rēķina numurs, nokārtošana vispirms piedāvā šī rēķina iemaksas. Atkārtota sasaistīšana nekad neko nenokārto.',
        'Importam, atkārtotai sasaistīšanai un dzēšanai nepieciešamas rakstīšanas tiesības PCT un piekļuve GrECo Latvia uzņēmumam; tikai lasīt varošie lietotāji var pārlūkot rēķinus, bet serveris šīs izmaiņas noraida.',
      ],
    },
  },
};
